import { ApplicationRef, ElementRef, Injectable, Type, ViewContainerRef } from '@angular/core';
import { SimpleTooltipComponent } from '../components/simple-tooltip/simple-tooltip.component';
import { TooltipComponent } from '../components/tooltip/tooltip.component';
import { TooltipPosition } from '../interfaces/tooltip.interface';

@Injectable({
    providedIn: 'root',
})
export class TooltipService {
    private static tooltipRef: TooltipComponent;
    private hostViewChildRef!: ViewContainerRef;

    constructor(private applicationRef: ApplicationRef) {}

    initHost(viewChildRef: ViewContainerRef) {
        // Get the root view container ref directly via the initHost method called from the root app component
        this.hostViewChildRef = viewChildRef;
        // Insert the modal component into the root view container
        const componentRef = this.hostViewChildRef.createComponent(TooltipComponent);
        // Get the instance of the modal component
        TooltipService.tooltipRef = componentRef.instance;
    }

    /**
     * Opens a tooltip containing the specified component and supplies data to it
     * @param component - The class name of a component to be displayed in the tooltip. It should be a class that extends BaseTooltip
     * @param elementRef - The activator element's elementRef
     * @param data - Data to be passed to the tooltip
     * @param position - Where to position the tooltip relative to the anchor. Defaults to 'right'.
     */
    public showTooltip(
        component: Type<any>,
        elementRef: ElementRef,
        data?: any,
        position: TooltipPosition = 'right',
        showHtmlContent: boolean = false,
        tooltipTextWidthCharacters: number = 120,
        onShowCallback: () => void = () => {},
        onHideCallback: () => void = () => {},
    ) {
        TooltipService.tooltipRef.showTooltip(
            component,
            elementRef,
            data,
            position,
            showHtmlContent,
            tooltipTextWidthCharacters,
            onShowCallback,
            onHideCallback,
        );
    }

    /**
     * Opens the SimpleTooltipComponent as a basic tooltip and supplies what is passed in as tooltipText to it.
     * @param elementRef - The activator element's elementRef
     * @param tooltipText - The text to be displayed as a tooltip
     * @param position - Where to position the tooltip relative to the anchor. Defaults to 'right'.
     */
    public showSimpleTooltip(
        elementRef: ElementRef,
        tooltipText: string,
        position: TooltipPosition = 'right',
        showHtmlContent: boolean = false,
        tooltipTextWidthCharacters: number = 120,
        onShowCallback: () => void = () => {},
        onHideCallback: () => void = () => {},
    ) {
        TooltipService.tooltipRef.showTooltip(
            SimpleTooltipComponent,
            elementRef,
            { tooltipText },
            position,
            showHtmlContent,
            tooltipTextWidthCharacters,
            onShowCallback,
            onHideCallback,
        );
    }

    /**
     * Closes the tooltip
     */
    public hideTooltip(): void {
        TooltipService.tooltipRef.hideTooltip();
    }

    public isTooltipOpen(): boolean {
        return TooltipService.tooltipRef.isTooltipOpen();
    }
}
